import React, { useEffect, useRef, useState } from "react";
import { Save, Loader2, Plus, Trash2, Upload, User2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

export function GuestSpeakers({ eventId }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRefs = useRef({});

  const load = () => api.get(`/events/${eventId}/guest-speakers`)
    .then((r) => setItems(r.data.items || []))
    .catch((e) => toast.error(errText(e)));
  useEffect(() => { setItems(null); load(); }, [eventId]); // eslint-disable-line

  const update = (idx, patch) => setItems(items.map((g, i) => i === idx ? { ...g, ...patch } : g));
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const add = () => {
    if ((items || []).length >= 3) return toast.error("You can feature up to 3 guest speakers");
    setItems([...(items || []), { id: "", name: "", title: "", photo_url: "" }]);
  };

  const uploadPhoto = async (idx, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Photo is too large — pick one under 4 MB");
    setBusy(`upload-${idx}`);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const { data } = await api.post(`/events/${eventId}/guest-speakers/photo`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      update(idx, { photo_url: data.url });
      toast.success("Photo uploaded");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  const save = async () => {
    for (const g of items) {
      if (!g.name.trim() || !g.title.trim()) return toast.error("Every speaker needs a name and title");
      if (!g.photo_url) return toast.error(`Please upload a photo for ${g.name || "the speaker"}`);
    }
    setBusy("save");
    try {
      const { data } = await api.put(`/events/${eventId}/guest-speakers`, { items });
      setItems(data.items || []);
      toast.success("Guest speakers saved");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  if (items === null) return <div className="mono-label" data-testid="guests-loading">Loading guest speakers…</div>;

  return (
    <Panel className="p-6" data-testid="guest-speakers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonoLabel><User2 size={13} className="inline mr-2" />Guest Speakers · About the Event section</MonoLabel>
        <button data-testid="guests-add" onClick={add} className="btn-ghost" disabled={(items || []).length >= 3}>
          <Plus size={14} /> Add speaker
        </button>
      </div>

      <div className="mt-6 space-y-5">
        {items.length === 0 && (
          <div className="mono-label" style={{ color: "var(--taupe)" }}>
            No speakers yet — add up to 3. They appear in the "About the Event" section on /register.
          </div>
        )}
        {items.map((g, i) => (
          <div key={i} className="hairline p-4 flex flex-col sm:flex-row gap-4" data-testid={`guest-row-${i}`}>
            <div className="sm:w-32">
              <div
                className="w-28 h-28 bg-[var(--panel-2)] border overflow-hidden flex items-center justify-center cursor-pointer"
                style={{ borderColor: g.photo_url ? "var(--hairline)" : "rgba(226,62,71,0.45)" }}
                onClick={() => fileRefs.current[i]?.click()}
                data-testid={`guest-photo-${i}`}
              >
                {g.photo_url
                  ? <img src={g.photo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${g.photo_url}` : g.photo_url} alt={g.name} className="w-full h-full object-cover" />
                  : <User2 size={28} style={{ color: "var(--taupe)" }} />}
              </div>
              <input
                ref={(el) => { fileRefs.current[i] = el; }}
                type="file" accept="image/*" className="hidden"
                data-testid={`guest-file-${i}`}
                onChange={(e) => uploadPhoto(i, e.target.files?.[0])}
              />
              <button className="btn-ghost !py-1 !px-2 !text-xs mt-2 w-full justify-center" data-testid={`guest-upload-${i}`}
                disabled={busy === `upload-${i}`} onClick={() => fileRefs.current[i]?.click()}>
                {busy === `upload-${i}` ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {g.photo_url ? "Replace" : "Upload"}
              </button>
            </div>

            <div className="flex-1 grid grid-cols-1 gap-3">
              <label className="field-label">
                <span>Name</span>
                <input className="input-x !py-2" value={g.name} data-testid={`guest-name-${i}`}
                  onChange={(e) => update(i, { name: e.target.value })} placeholder="Mac Srinivasan" />
              </label>
              <label className="field-label">
                <span>Title / Affiliation</span>
                <input className="input-x !py-2" value={g.title} data-testid={`guest-title-${i}`}
                  onChange={(e) => update(i, { title: e.target.value })} placeholder="Global Markets President, BNI" />
              </label>
            </div>

            <div className="flex sm:flex-col items-start sm:items-end">
              <button data-testid={`guest-del-${i}`} onClick={() => remove(i)}
                className="p-2 hover:text-[var(--red)]" title="Remove">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button data-testid="guests-save" className="btn-red" disabled={!!busy} onClick={save}>
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save speakers
        </button>
      </div>
      <div className="mono-label mt-4" style={{ color: "var(--muted)" }}>
        Up to 3 speakers show on the public page. Each photo should be a square portrait (JPG/PNG) under 4 MB.
      </div>
    </Panel>
  );
}
